import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';
import { Spinner } from './primitives';

const buttonVariants = cva(
  // `nx-btn` clears the UA button chrome — see design-tokens.css for why
  // preflight is not loaded and the reset is opt-in.
  'nx-btn nx-focusable tw:inline-flex tw:items-center tw:justify-center tw:gap-1.5 tw:whitespace-nowrap tw:rounded-md tw:text-xs tw:font-semibold tw:transition-colors tw:duration-200 tw:disabled:pointer-events-none tw:disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'tw:bg-nx-primary tw:text-nx-text-on-accent tw:hover:bg-nx-primary-hover',
        outline: 'tw:border tw:border-nx-border tw:bg-nx-surface tw:text-nx-text tw:hover:bg-nx-surface-hover',
        ghost: 'tw:bg-transparent tw:text-nx-text-muted tw:hover:bg-nx-surface-hover',
        danger: 'tw:bg-nx-error tw:text-nx-text-on-accent tw:hover:opacity-90',
      },
      size: {
        sm: 'tw:h-7 tw:px-2.5 tw:text-xs',
        default: 'tw:h-8 tw:px-3',
      },
      fullWidth: { true: 'tw:w-full', false: '' },
    },
    defaultVariants: { variant: 'default', size: 'default', fullWidth: false },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and disables the button. The label stays visible so the button does not change width mid-action. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, fullWidth, asChild, loading, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, fullWidth }), 'tw:cursor-pointer tw:disabled:cursor-default', className)}
      {...props}
    >
      {/*
        WITH `asChild`, THE SLOT MUST RECEIVE EXACTLY ONE ELEMENT.
        
        This used to pass two children unconditionally — the spinner slot and
        `children` — and a falsy `{loading && …}` still counts as a child. So
        every `<Button asChild>` threw "Slot failed to slot onto its children",
        crashing the whole route rather than the button, whether or not it was
        loading. It took out the Patient List, which renders a history dialog
        with an `asChild` anchor.

        A fragment would not fix it: Slot merges its props onto the child
        ELEMENT, so wrapping would break the very thing `asChild` is for. When
        composing, the child passes through alone — a loading state belongs to
        a button the component owns, not to someone else's anchor.
      */}
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Spinner size={12} label="" className="tw:-ml-0.5" />}
          {children}
        </>
      )}
    </Comp>
  );
});
